import { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import CONFIG, { CREDIT_PREDICTION, CREDIT_FALLBACK } from '../config';
import { fetchWithAuth } from '../utils/api';
import { downloadFile } from '../utils/downloadUtils';
import {
  fetchAllSlideTypoResults,
  subscribeToTypoAnalysisProgress
} from '../services/typoResultService';
import { loadTypoDraft, deleteTypoDraft } from '../services/draftService';

const useTypoAnalysis = (
  selectedPpt,
  userId,
  slides,
  credit,
  predictionData,
  showAlertModal,
  showConfirmModal,
  onAnalysisComplete,
  t // i18n 번역 함수
) => {
  const CLOUD_FUNCTION_URL = CONFIG.CLOUD_FUNCTION_URL;

  // 개별 슬라이드 검사 관련 상태
  const [selectedSlideTypos, setSelectedSlideTypos] = useState({});
  const [applyingTypoCorrections, setApplyingTypoCorrections] = useState(false);
  const [editedSuggestions, setEditedSuggestions] = useState({});

  const [slideTypoResults, setSlideTypoResults] = useState({});
  const [slideTypoCheckStatus, setSlideTypoCheckStatus] = useState({});

  // 전체 슬라이드 검사 관련 상태
  const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
  const [checkAllProgress, setCheckAllProgress] = useState({
    status: '',
    percent: 0,
    completed: false
  });

  // 결과 로딩 상태
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  // Draft 존재 여부
  const [hasTypoDraft, setHasTypoDraft] = useState(false);
  // 로드된 Draft 데이터 (모달 열 때 확인 후 적용)
  const [loadedTypoDraft, setLoadedTypoDraft] = useState(null);

  // 리스너 참조 저장
  const unsubscribeRef = useRef(null);
  // 콜백 ref (의존성 루프 방지)
  const callbacksRef = useRef({ showAlertModal, showConfirmModal, onAnalysisComplete, t });
  callbacksRef.current = { showAlertModal, showConfirmModal, onAnalysisComplete, t };

  // 진행상황 업데이트 핸들러 (전체 검사용)
  const handleProgressUpdate = useCallback(async (progressData) => {
    const { showAlertModal, showConfirmModal, onAnalysisComplete, t } = callbacksRef.current;

    // 에러 발생 시
    if (progressData.error) {
      console.log('[useTypoAnalysis] 검사 에러:', progressData.error);

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      setIsAnalyzingAll(false);
      setCheckAllProgress({ status: '', percent: 0, completed: false });

      showAlertModal(t('hooks.typo.errorDuringCheck', { error: progressData.error }), 'error');
      return;
    }

    // 진행상황 업데이트
    setCheckAllProgress({
      status: progressData.status || '',
      percent: progressData.percent || 0,
      completed: progressData.completed || false
    });

    // 완료 시 결과 로드 + 모달 표시
    if (progressData.completed) {
      console.log('[useTypoAnalysis] 검사 완료, 결과 로드 중...');

      // 리스너 해제
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      try {
        // Firestore에서 결과 로드
        const results = await fetchAllSlideTypoResults(selectedPpt);

        if (results && results.slides && results.slides.length > 0) {
          const newResults = {};
          const newStatus = {};

          results.slides.forEach(slide => {
            const idx = slide.slide_index;
            newResults[idx] = slide.typos || [];
            newStatus[idx] = {
              typoCount: slide.typos ? slide.typos.length : 0,
              checking: false,
              hasBeenChecked: slide.has_typo_check || false
            };
          });

          setSlideTypoResults(newResults);
          setSlideTypoCheckStatus(newStatus);
        }

        // 모달 표시 전에 스피너 중지
        setIsAnalyzingAll(false);

        // 완료 모달 표시 (비동기 처리)
        const totalTypos = progressData.total_typos || 0;
        const usedCredit = progressData.total_cost_krw || 0;
        showConfirmModal(
          t('hooks.typo.checkAllComplete', {
            slideCount: progressData.successful_slides || 0,
            typoCount: totalTypos,
            usedCredit: Math.ceil(usedCredit)
          })
        ).then((confirmed) => {
          if (confirmed && onAnalysisComplete) {
            onAnalysisComplete('typo');
          }
        });
      } catch (error) {
        console.error('[useTypoAnalysis] 결과 로드 오류:', error);
        setIsAnalyzingAll(false);
      }
    }
  }, [selectedPpt]);

  // PPT 변경 시 상태 초기화, 기존 결과 로드, 진행 중인 검사 복구
  useEffect(() => {
    const loadExistingResults = async () => {
      // 기존 리스너 해제
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      // PPT 변경 시 모든 상태 초기화
      setEditedSuggestions({});
      setSelectedSlideTypos({});

      if (!selectedPpt || !userId) {
        setSlideTypoResults({});
        setSlideTypoCheckStatus({});
        setCheckAllProgress({ status: '', percent: 0, completed: false });
        setIsAnalyzingAll(false);
        return;
      }

      try {
        setIsLoadingResults(true);

        // 1. 진행 중인 검사 확인
        const progressRef = doc(db, 'PPT_FILE', selectedPpt, 'typo_results', 'analysis_progress');
        const progressSnap = await getDoc(progressRef);

        if (progressSnap.exists()) {
          const progressData = progressSnap.data();

          // 진행 중인 검사가 있는 경우 (완료되지 않고, 에러도 없는 경우)
          if (!progressData.completed && !progressData.error && progressData.status) {
            console.log('[useTypoAnalysis] 진행 중인 오타 검사 발견, 리스너 연결');

            setIsAnalyzingAll(true);
            setCheckAllProgress({
              status: progressData.status || '',
              percent: progressData.percent || 0,
              completed: false
            });

            // 리스너 시작
            unsubscribeRef.current = subscribeToTypoAnalysisProgress(selectedPpt, handleProgressUpdate);
            setIsLoadingResults(false);
            return;
          }
        }

        // 2. 완료된 결과 로드
        const existingResults = await fetchAllSlideTypoResults(selectedPpt);

        if (existingResults && existingResults.slides && existingResults.slides.length > 0) {
          const newResults = {};
          const newStatus = {};

          existingResults.slides.forEach(slide => {
            const idx = slide.slide_index;
            newResults[idx] = slide.typos || [];
            newStatus[idx] = {
              typoCount: slide.typos ? slide.typos.length : 0,
              checking: false,
              hasBeenChecked: slide.has_typo_check || false
            };
          });

          setSlideTypoResults(newResults);
          setSlideTypoCheckStatus(newStatus);

          // 3. Draft 로드 (검사 결과가 있는 경우에만) - 바로 적용하지 않고 저장만
          const draft = await loadTypoDraft(selectedPpt, userId);
          if (draft) {
            const hasSelectedTypos = draft.selected_typos && Object.keys(draft.selected_typos).length > 0;
            const hasEditedSuggestions = draft.edited_suggestions && Object.keys(draft.edited_suggestions).length > 0;

            if (hasSelectedTypos || hasEditedSuggestions) {
              setHasTypoDraft(true);
              // 숫자 키로 변환 (Firestore에서 문자열로 저장될 수 있음)
              const convertedSelectedTypos = {};
              if (hasSelectedTypos) {
                Object.entries(draft.selected_typos).forEach(([slideIdx, typoIndices]) => {
                  convertedSelectedTypos[parseInt(slideIdx)] = typoIndices;
                });
              }
              // draft 데이터를 저장만 하고 바로 적용하지 않음
              setLoadedTypoDraft({
                selected_typos: hasSelectedTypos ? convertedSelectedTypos : {},
                edited_suggestions: hasEditedSuggestions ? draft.edited_suggestions : {}
              });
              console.log('[useTypoAnalysis] 오타 draft 로드됨 (적용 대기 중)');
            } else {
              setHasTypoDraft(false);
              setLoadedTypoDraft(null);
            }
          } else {
            setHasTypoDraft(false);
            setLoadedTypoDraft(null);
          }
        } else {
          setSlideTypoResults({});
          setSlideTypoCheckStatus({});
          setHasTypoDraft(false);
          setLoadedTypoDraft(null);
        }
      } catch (error) {
        console.error('[useTypoAnalysis] 기존 결과 로드 오류:', error);
        setSlideTypoResults({});
        setSlideTypoCheckStatus({});
        setHasTypoDraft(false);
        setLoadedTypoDraft(null);
      } finally {
        setIsLoadingResults(false);
      }
    };

    loadExistingResults();

    // 컴포넌트 언마운트 시 리스너 해제
    return () => {
      if (unsubscribeRef.current) {
        console.log('[useTypoAnalysis] 리스너 해제 (언마운트)');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [selectedPpt, userId, handleProgressUpdate]);

  // 전체 슬라이드 오타 검사 (비동기)
  const checkAllSlidesTypos = useCallback(async () => {
    const { showAlertModal, showConfirmModal, t } = callbacksRef.current;

    console.log('[DEBUG] checkAllSlidesTypos 함수 호출됨');

    if (!selectedPpt || !userId) {
      showAlertModal(t('hooks.typo.noDocOrUser'), 'error');
      return;
    }

    if (!slides || slides.length === 0) {
      showAlertModal(t('hooks.typo.noSlides'), 'warning');
      return;
    }

    // Credit 체크 - 예측 기반 또는 fallback
    const requiredCredit = predictionData?.typo?.batch_tokens_sum
      ? Math.ceil(predictionData.typo.batch_tokens_sum * CREDIT_PREDICTION.TYPO_BATCH_RATE) * CREDIT_PREDICTION.MULTIPLIER
      : CREDIT_FALLBACK.TYPO_BATCH;

    if (credit < requiredCredit) {
      showAlertModal(t('hooks.typo.insufficientCreditAll'), 'warning');
      return;
    }

    // 이미 검사된 슬라이드가 있는지 확인
    const hasCheckedSlides = Object.values(slideTypoCheckStatus).some(status => status.hasBeenChecked);
    if (hasCheckedSlides) {
      const confirmed = await showConfirmModal(t('hooks.typo.recheckConfirm'));
      if (!confirmed) return;
    }

    setIsAnalyzingAll(true);
    setCheckAllProgress({ status: 'preparing', percent: 0, completed: false });

    // 새로운 분석 시작 시 기존 draft 삭제
    await deleteTypoDraft(selectedPpt);
    setHasTypoDraft(false);

    try {
      // 기존 리스너 해제
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      // API 호출 (즉시 응답)
      const response = await fetchWithAuth(`${CLOUD_FUNCTION_URL}/check_all_slides_typos`, {
        method: 'POST',
        body: JSON.stringify({
          doc_id: selectedPpt,
          userId: userId,
          confidence_threshold: 9
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      // 즉시 응답 수신 - 백그라운드 처리 시작됨
      if (data.success) {
        console.log('[useTypoAnalysis] 오타 검사 시작됨:', data);

        // 리스너 시작 (완료까지 유지)
        unsubscribeRef.current = subscribeToTypoAnalysisProgress(selectedPpt, handleProgressUpdate);
      }

    } catch (error) {
      console.error('오타 검사 시작 오류:', error);

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      setCheckAllProgress({ status: '', percent: 0, completed: false });
      setIsAnalyzingAll(false);

      showAlertModal(error.message || t('hooks.typo.checkError'), 'error');
    }
  }, [selectedPpt, userId, slides, credit, predictionData, slideTypoCheckStatus, CLOUD_FUNCTION_URL, handleProgressUpdate]);

  // 특정 슬라이드 오타 검사 (동기 - 완료 후 모달 표시)
  // options.silent: true이면 완료 알림창을 표시하지 않음 (모달에서 호출 시)
  const checkSlideTypos = useCallback(async (slideIndex, options = {}) => {
    const { silent = false } = options;
    const { showAlertModal, showConfirmModal, onAnalysisComplete, t } = callbacksRef.current;

    // Credit 체크 - 예측 기반 또는 fallback
    const slidePromptTokens = predictionData?.slide_stats?.[slideIndex]?.prompt_tokens;
    const requiredCredit = slidePromptTokens
      ? Math.ceil(slidePromptTokens * CREDIT_PREDICTION.TYPO_INDIVIDUAL_RATE) * CREDIT_PREDICTION.MULTIPLIER
      : CREDIT_FALLBACK.TYPO_INDIVIDUAL;

    if (credit < requiredCredit) {
      showAlertModal(t('hooks.typo.insufficientCreditSlide'), 'warning');
      return;
    }

    // 재검사 확인 (silent 모드가 아닐 때만)
    if (!silent && (slides[slideIndex]?.typo_checked || slideTypoCheckStatus[slideIndex]?.hasBeenChecked)) {
      const confirmed = await showConfirmModal(t('hooks.typo.recheckSlideConfirm'));
      if (!confirmed) return;
    }

    if (!slides[slideIndex] || !slides[slideIndex].hasText) {
      showAlertModal(t('hooks.typo.noText'), 'warning');
      return;
    }

    setSlideTypoCheckStatus(prev => ({
      ...prev,
      [slideIndex]: {
        typoCount: 0,
        checking: true,
        hasBeenChecked: false
      }
    }));

    try {
      const res = await fetchWithAuth(`${CLOUD_FUNCTION_URL}/check_slide_typos`, {
        method: 'POST',
        body: JSON.stringify({
          doc_id: selectedPpt,
          slide_index: slideIndex,
          userId: userId
        })
      });

      const data = await res.json();

      const typosFound = data.typos || [];

      setSlideTypoResults(prev => ({
        ...prev,
        [slideIndex]: typosFound
      }));

      setSelectedSlideTypos(prev => ({
        ...prev,
        [slideIndex]: []
      }));

      setSlideTypoCheckStatus(prev => ({
        ...prev,
        [slideIndex]: {
          typoCount: typosFound.length,
          checking: false,
          hasBeenChecked: true
        }
      }));

      // 완료 모달 표시 (silent 모드가 아닐 때만)
      if (!silent) {
        const confirmed = await showConfirmModal(
          t('hooks.typo.slideCheckComplete', {
            slideNum: slideIndex + 1,
            typoCount: typosFound.length
          })
        );

        if (confirmed && onAnalysisComplete) {
          onAnalysisComplete('typo');
        }
      }

    } catch (e) {
      showAlertModal(t('hooks.typo.slideCheckError', { slideNum: slideIndex + 1, error: e.message }), 'error');

      setSlideTypoCheckStatus(prev => ({
        ...prev,
        [slideIndex]: {
          typoCount: 0,
          checking: false,
          hasBeenChecked: false
        }
      }));
    }
  }, [selectedPpt, userId, slides, credit, predictionData, slideTypoCheckStatus, CLOUD_FUNCTION_URL]);

  // 오타 선택 토글
  const toggleSlideTypoSelection = useCallback((slideIndex, typoIndex) => {
    setSelectedSlideTypos(prev => {
      const slideSelected = prev[slideIndex] || [];
      if (slideSelected.includes(typoIndex)) {
        return {
          ...prev,
          [slideIndex]: slideSelected.filter(idx => idx !== typoIndex)
        };
      } else {
        return {
          ...prev,
          [slideIndex]: [...slideSelected, typoIndex]
        };
      }
    });
  }, []);

  // 슬라이드별 오타 전체선택
  const selectAllSlideTypos = useCallback((slideIndex) => {
    const slideResults = slideTypoResults[slideIndex] || [];
    const allIndices = slideResults.map((_, index) => index);

    setSelectedSlideTypos(prev => ({
      ...prev,
      [slideIndex]: allIndices
    }));
  }, [slideTypoResults]);

  // 슬라이드별 오타 전체해제
  const deselectAllSlideTypos = useCallback((slideIndex) => {
    setSelectedSlideTypos(prev => ({
      ...prev,
      [slideIndex]: []
    }));
  }, []);

  // 오타 수정 제안 편집
  const updateEditedSuggestion = useCallback((type, id, suggestion) => {
    setEditedSuggestions(prev => ({
      ...prev,
      [`${type}_${id}`]: suggestion
    }));
  }, []);

  const getEditedSuggestion = useCallback((type, id, originalSuggestion) => {
    const key = `${type}_${id}`;
    return editedSuggestions.hasOwnProperty(key) ? editedSuggestions[key] : originalSuggestion;
  }, [editedSuggestions]);

  // 슬라이드 오타 수정 적용
  const applySlideTypoCorrections = useCallback(async () => {
    const { showAlertModal, showConfirmModal, t } = callbacksRef.current;

    const hasSelectedTypos = Object.values(selectedSlideTypos).some(selected => selected.length > 0);

    if (!hasSelectedTypos) {
      showAlertModal(t('hooks.typo.selectTypoFirst'), 'warning');
      return;
    }

    setApplyingTypoCorrections(true);

    try {
      const allSelectedTypos = [];
      Object.entries(selectedSlideTypos).forEach(([slideIndex, typoIndices]) => {
        const slideResults = slideTypoResults[slideIndex] || [];
        typoIndices.forEach(typoIndex => {
          if (slideResults[typoIndex]) {
            const originalTypo = slideResults[typoIndex];
            const editedSuggestion = getEditedSuggestion('slide', `${slideIndex}_${typoIndex}`, originalTypo.suggestion);
            allSelectedTypos.push({
              ...originalTypo,
              suggestion: editedSuggestion,
              slide_index: parseInt(slideIndex)
            });
          }
        });
      });

      const res = await fetchWithAuth(`${CLOUD_FUNCTION_URL}/modify_typos`, {
        method: 'POST',
        body: JSON.stringify({
          doc_id: selectedPpt,
          selected_typos: allSelectedTypos,
          userId: userId
        })
      });

      const data = await res.json();

      if (data.error) {
        showAlertModal(t('hooks.typo.modifyError', { error: data.error }), 'error');
        return data;
      }

      if (data.success && data.download_url) {
        // 성공 메시지와 함께 확인/취소 선택
        const message = t('hooks.typo.modifySuccess', { count: allSelectedTypos.length });
        const downloadConfirm = await showConfirmModal(message);

        if (downloadConfirm) {
          // 확인 클릭 시 다운로드 (Blob 방식으로 Edge 호환)
          await downloadFile(data.download_url, data.filename);
        }

        return {
          download_url: data.download_url,
          filename: data.filename,
          modified_typos_count: data.modified_typos_count
        };
      } else {
        showAlertModal(t('hooks.typo.noDownloadLink'), 'warning');
        return null;
      }
    } catch (e) {
      showAlertModal(t('hooks.typo.modifyError', { error: e.message }), 'error');
      return null;
    } finally {
      setApplyingTypoCorrections(false);
    }
  }, [selectedPpt, userId, selectedSlideTypos, slideTypoResults, getEditedSuggestion, CLOUD_FUNCTION_URL]);

  // 상태 초기화 함수들
  const resetTypoStates = useCallback(() => {
    setSlideTypoResults({});
    setSlideTypoCheckStatus({});
    setSelectedSlideTypos({});
    setEditedSuggestions({});
    setIsAnalyzingAll(false);
    setCheckAllProgress({
      status: '',
      percent: 0,
      completed: false
    });
  }, []);

  const initializeSlideTypoStatus = useCallback((slideCount) => {
    const initialCheckStatus = {};
    for (let i = 0; i < slideCount; i++) {
      initialCheckStatus[i] = {
        typoCount: 0,
        checking: false,
        hasBeenChecked: false
      };
    }
    setSlideTypoCheckStatus(initialCheckStatus);
  }, []);

  // 개별 슬라이드 중 하나라도 검사 중인지 확인
  const isAnySingleSlideChecking = Object.values(slideTypoCheckStatus).some(status => status?.checking);

  // Draft 적용 함수 (모달에서 확인 시 호출)
  const applyTypoDraft = useCallback(() => {
    console.log('[useTypoAnalysis] applyTypoDraft 호출됨, loadedTypoDraft:', loadedTypoDraft);
    if (loadedTypoDraft) {
      if (loadedTypoDraft.selected_typos && Object.keys(loadedTypoDraft.selected_typos).length > 0) {
        console.log('[useTypoAnalysis] selected_typos 적용:', loadedTypoDraft.selected_typos);
        setSelectedSlideTypos(loadedTypoDraft.selected_typos);
      }
      if (loadedTypoDraft.edited_suggestions && Object.keys(loadedTypoDraft.edited_suggestions).length > 0) {
        console.log('[useTypoAnalysis] edited_suggestions 적용:', loadedTypoDraft.edited_suggestions);
        setEditedSuggestions(loadedTypoDraft.edited_suggestions);
      }
      console.log('[useTypoAnalysis] 오타 draft 적용됨');
    } else {
      console.log('[useTypoAnalysis] loadedTypoDraft가 null입니다');
    }
  }, [loadedTypoDraft]);

  // Draft 초기화 함수 (모달에서 취소 시 호출)
  const clearTypoDraft = useCallback(() => {
    setLoadedTypoDraft(null);
    setHasTypoDraft(false);
    console.log('[useTypoAnalysis] 오타 draft 초기화됨');
  }, []);

  return {
    // 상태
    selectedSlideTypos,
    applyingTypoCorrections,
    editedSuggestions,
    slideTypoResults,
    slideTypoCheckStatus,
    isAnalyzingAll,
    isAnySingleSlideChecking,
    checkAllProgress,
    isLoadingResults,
    hasTypoDraft,
    loadedTypoDraft,

    // 함수
    checkAllSlidesTypos,
    checkSlideTypos,
    toggleSlideTypoSelection,
    selectAllSlideTypos,
    deselectAllSlideTypos,
    updateEditedSuggestion,
    getEditedSuggestion,
    applySlideTypoCorrections,
    resetTypoStates,
    initializeSlideTypoStatus,
    applyTypoDraft,
    clearTypoDraft,

    // setter 함수들 (필요한 경우)
    setSlideTypoResults,
    setSlideTypoCheckStatus,
    setSelectedSlideTypos,
    setEditedSuggestions
  };
};

export default useTypoAnalysis;
